import * as React from "react";
import { useEffect, useMemo } from "react";
import { isNil, isNotNil } from "ramda";
import { styled } from "styled-components";

import { List } from "affinityUI/data/list";
import { Summary } from "affinityUI/data/summary";
import { DateTime } from "affinityUI/data/valueDisplay";
import { Grid } from "affinityUI/elements/grid";
import { Loading } from "affinityUI/elements/loading";
import { Tag, TagType } from "affinityUI/elements/tag";
import { useExhWbcResults } from "components/pages/patients/patients/hooks/useExhWbcResults";
import { useHospitalConfiguration } from "components/pages/settings/context";
import { UnitGranulocyte, UnitWBC } from "components/views/patientDetails/ui/resultUnits";
import { useAppDispatch, warningSlice } from "lib/store";
import { Measurement, PatientResult, PatientsListEntry } from "types/patient";

import { Column } from "./components/column";
import { Result } from "./components/result";
import { WarningTag } from "./components/warningTag";

export enum STATUS {
  Warn = "WARN",
  Alert = "ALERT",
}

const ResultTag = styled(Tag)`
  margin: 0 0 0.25rem 0;
`;

const getStatus = (measurement?: Measurement, confirmatoryTest?: boolean): STATUS | undefined => {
  if (confirmatoryTest) {
    return STATUS.Alert;
  }
  if (measurement?.measurementInterval?.outOfRange) {
    return STATUS.Warn;
  }
};

export const getTag = (
  daysAgo?: Maybe<number>
): {
  timeAgo: string;
  tagType: TagType;
} => {
  if (isNil(daysAgo))
    return {
      timeAgo: "Never tested",
      tagType: TagType.neutral,
    };

  let timeAgo;
  let tagType: TagType;
  if (daysAgo >= 7) {
    // Red - 7 days
    tagType = TagType.negative;
  } else if (daysAgo < 7 && daysAgo >= 2) {
    // Amber - 48 hours to 7 days
    tagType = TagType.warn;
  } else {
    // Green - up to 48 hours
    tagType = TagType.positive;
  }

  if (daysAgo < 1) {
    timeAgo = "Today";
  } else if (daysAgo < 2) {
    timeAgo = "Yesterday";
  } else {
    timeAgo = `${daysAgo} days ago`;
  }

  return {
    timeAgo,
    tagType,
  };
};

export type ResultCardProps = {
  patient: PatientsListEntry;
};

export const ResultCard: React.FC<ResultCardProps> = ({ patient }) => {
  const {
    id,
    lastName,
    firstName,
    dateOfBirth,
    hospitalNumber,
    deviceMeasurementIds,
    latestResult: latestResultFromTestSuite,
  } = patient;

  const { hospitalConfig, normalRanges } = useHospitalConfiguration();

  const { results, loading } = useExhWbcResults(
    latestResultFromTestSuite !== null && latestResultFromTestSuite !== undefined ? undefined : deviceMeasurementIds
  );

  const dispatch = useAppDispatch();
  const latestResult = useMemo<Maybe<PatientResult>>(() => {
    if (isNotNil(latestResultFromTestSuite)) {
      return latestResultFromTestSuite;
    }
    return results[0] ?? null;
  }, [latestResultFromTestSuite, results]);

  useEffect(() => {
    if (latestResult?.belowNeutrophilThreshold) {
      dispatch(warningSlice.actions.setPatientneutrophilWarning({ ids: [id] }));
    } else {
      dispatch(warningSlice.actions.removePatientneutrophilWarning({ ids: [id] }));
    }
    return () => {
      dispatch(warningSlice.actions.removePatientneutrophilWarning({ ids: [id] }));
    };
  }, [dispatch, id, latestResult?.belowNeutrophilThreshold]);

  const dateRecorded = latestResult ? latestResult.dateRecorded : "-";
  const tag = useMemo(() => (latestResult ? getTag(latestResult.daysAgo) : null), [latestResult]);
  const href = `${patient.id}`;

  const meta = useMemo(
    () => (
      <>
        {dateOfBirth && (
          <Summary.Item label="DOB:">
            <DateTime value={dateOfBirth} format={DateTime.FORMAT.DATE} />
          </Summary.Item>
        )}
        <Summary.Item label="HOSP. no:">{hospitalNumber}</Summary.Item>
        <Summary.Item label="Patient ID:">{patient.entiaPatientId}</Summary.Item>
      </>
    ),
    [dateOfBirth, hospitalNumber, patient.entiaPatientId]
  );

  return (
    <List.Item
      data-testid="patient-result-card"
      title={`${lastName}, ${firstName}`}
      meta={meta}
      tags={<WarningTag patient={patient} confirmatoryThreshold={hospitalConfig?.confirmatoryThreshold || null} />}
      aria-label={`View ${patient.lastName}, ${patient.firstName}`}
      key={patient.id}
      linkTo={href}
    >
      {loading ? (
        <Loading />
      ) : (
        <Grid $tight $cols={[Grid.cols.EQUAL]}>
          {latestResult && tag !== null ? (
            <Column>
              <ResultTag type={tag.tagType} data-testid="result-when">
                {tag.timeAgo}
              </ResultTag>
              <div aria-label="time">
                <DateTime format={DateTime.FORMAT.TIME} value={dateRecorded} />
              </div>
              <div aria-label="date">
                <DateTime format={DateTime.FORMAT.DATE} value={dateRecorded} />
              </div>
            </Column>
          ) : (
            <Column>
              <ResultTag type={TagType.neutral} data-testid="result-when">
                Never tested
              </ResultTag>
              <div>Awaiting first result</div>
            </Column>
          )}
          <Result
            value={latestResult?.whiteBloodCells.value}
            label="WBC"
            status={getStatus(latestResult?.whiteBloodCells)}
            unit={<UnitWBC normalRange={normalRanges?.wbcRange} />}
            id={`wbc-${id}`}
          />
          <Result
            value={latestResult?.granulocytes.value}
            label="Granulocytes"
            status={getStatus(latestResult?.granulocytes)}
            unit={<UnitGranulocyte normalRange={normalRanges?.granulocyteRange} />}
            id={`granulocytes-${id}`}
          />
        </Grid>
      )}
    </List.Item>
  );
};
